'use client';

import * as React from 'react';
import { MapPin, Users, Calendar, X } from 'lucide-react';

import { Badge } from '@workspace/ui/components/badge';
import { Button } from '@workspace/ui/components/button';
import { ScrollArea, ScrollBar } from '@workspace/ui/components/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@workspace/ui/components/table';

import { type WeddingSampleDto, type WeddingSampleLineItem } from '~/data/budget-explorer/get-public-wedding-samples';

// Category display labels
const BUDGET_CATEGORY_LABELS: Record<string, string> = {
  VENUE: 'Venue',
  CATERING: 'Catering',
  PHOTOGRAPHY: 'Photography',
  VIDEOGRAPHY: 'Videography',
  FLORALS: 'Florals',
  MUSIC: 'Music & Entertainment',
  ATTIRE: 'Attire',
  BEAUTY: 'Beauty',
  STATIONERY: 'Stationery',
  TRANSPORTATION: 'Transportation',
  DECOR: 'Decor',
  RENTALS: 'Rentals',
  FAVORS: 'Favors & Gifts',
  OFFICIANT: 'Officiant',
  WEDDING_PLANNER: 'Wedding Planner',
  OTHER: 'Other',
};

// Category colors
const CATEGORY_COLORS: Record<string, string> = {
  VENUE: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  CATERING: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  PHOTOGRAPHY: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  VIDEOGRAPHY: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
  FLORALS: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300',
  MUSIC: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  ATTIRE: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
  BEAUTY: 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/30 dark:text-fuchsia-300',
  STATIONERY: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  TRANSPORTATION: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-300',
  DECOR: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
  RENTALS: 'bg-lime-100 text-lime-800 dark:bg-lime-900/30 dark:text-lime-300',
  FAVORS: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  OFFICIANT: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  WEDDING_PLANNER: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
  OTHER: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
};

// Category order for consistent display
const CATEGORY_ORDER = [
  'VENUE',
  'CATERING',
  'PHOTOGRAPHY',
  'VIDEOGRAPHY',
  'FLORALS',
  'MUSIC',
  'DECOR',
  'RENTALS',
  'ATTIRE',
  'BEAUTY',
  'STATIONERY',
  'TRANSPORTATION',
  'FAVORS',
  'OFFICIANT',
  'WEDDING_PLANNER',
  'OTHER',
];

interface WeddingComparisonProps {
  weddings: WeddingSampleDto[];
  onRemoveWedding?: (weddingId: string) => void;
  onBack?: () => void;
}

// Normalize item names for comparison (e.g., "Photographer" and "Photography" -> same)
function normalizeItemName(name: string): string {
  return name.toLowerCase().trim();
}

// Get all unique item names across all weddings, grouped by category
function getAllItemsGroupedByCategory(
  weddings: WeddingSampleDto[]
): { category: string; items: string[] }[] {
  const categoryItems: Record<string, Set<string>> = {};

  for (const wedding of weddings) {
    for (const item of wedding.lineItems) {
      if (!categoryItems[item.category]) {
        categoryItems[item.category] = new Set();
      }
      categoryItems[item.category].add(item.itemName);
    }
  }

  // Sort categories by predefined order
  return CATEGORY_ORDER
    .filter((cat) => categoryItems[cat]?.size > 0)
    .map((category) => ({
      category,
      items: Array.from(categoryItems[category] || []).sort(),
    }));
}

// Get item amount for a specific wedding and item name
function getItemAmount(
  wedding: WeddingSampleDto,
  category: string,
  itemName: string
): number | null {
  const item = wedding.lineItems.find(
    (i) => i.category === category && i.itemName === itemName
  );
  return item?.amount ?? null;
}

// Get category total for a wedding
function getCategoryTotal(wedding: WeddingSampleDto, category: string): number {
  return wedding.lineItems
    .filter((item) => item.category === category)
    .reduce((sum, item) => sum + item.amount, 0);
}

export function WeddingComparison({
  weddings,
  onRemoveWedding,
  onBack,
}: WeddingComparisonProps): React.JSX.Element {
  const groupedItems = React.useMemo(
    () => getAllItemsGroupedByCategory(weddings),
    [weddings]
  );

  const formatCurrency = (val: number | null) => {
    if (val === null) return '—';
    return `$${val.toLocaleString()}`;
  };

  if (weddings.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Select weddings to compare
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Back button */}
      {onBack && (
        <div className="shrink-0 pb-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="gap-2 h-7 text-xs">
            ← Back to list
          </Button>
        </div>
      )}

      <ScrollArea className="flex-1">
        <div className="min-w-[600px]">
          <Table>
            <TableHeader>
              {/* Wedding Headers */}
              <TableRow className="hover:bg-transparent border-b-2">
                <TableHead className="w-[180px] bg-background sticky left-0 z-10 font-bold text-xs">
                  Attribute
                </TableHead>
                {weddings.map((wedding, idx) => (
                  <TableHead
                    key={wedding.id}
                    className="min-w-[140px] bg-background text-center align-top"
                  >
                    <div className="py-1 space-y-1">
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-xs font-medium text-muted-foreground">
                          #{idx + 1}
                        </span>
                        {onRemoveWedding && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0"
                            onClick={() => onRemoveWedding(wedding.id)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                      <div className="text-base font-bold text-foreground font-mono">
                        ${wedding.totalSpend.toLocaleString()}
                      </div>
                      <div className="flex flex-wrap gap-1 justify-center">
                        <Badge variant="outline" className="text-[9px] px-1 py-0 gap-0.5">
                          <MapPin className="h-2.5 w-2.5" />
                          {wedding.location.split(',')[0]}
                        </Badge>
                        <Badge variant="outline" className="text-[9px] px-1 py-0 gap-0.5">
                          <Users className="h-2.5 w-2.5" />
                          {wedding.guestCount}
                        </Badge>
                      </div>
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {/* General Info Section */}
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableCell
                  colSpan={weddings.length + 1}
                  className="py-1.5 font-bold text-[10px] uppercase tracking-wider text-muted-foreground"
                >
                  General
                </TableCell>
              </TableRow>
              
              <TableRow>
                <TableCell className="text-xs font-medium text-muted-foreground sticky left-0 bg-background py-1.5">
                  Total Budget
                </TableCell>
                {weddings.map((wedding) => (
                  <TableCell key={wedding.id} className="text-center font-mono text-xs py-1.5 font-semibold">
                    ${wedding.totalSpend.toLocaleString()}
                  </TableCell>
                ))}
              </TableRow>

              <TableRow>
                <TableCell className="text-xs font-medium text-muted-foreground sticky left-0 bg-background py-1.5">
                  Guest Count
                </TableCell>
                {weddings.map((wedding) => (
                  <TableCell key={wedding.id} className="text-center text-xs py-1.5">
                    {wedding.guestCount}
                  </TableCell>
                ))}
              </TableRow>

              <TableRow>
                <TableCell className="text-xs font-medium text-muted-foreground sticky left-0 bg-background py-1.5">
                  Cost Per Guest
                </TableCell>
                {weddings.map((wedding) => (
                  <TableCell key={wedding.id} className="text-center font-mono text-xs py-1.5">
                    ${wedding.guestCount > 0 ? Math.round(wedding.totalSpend / wedding.guestCount).toLocaleString() : '—'}
                  </TableCell>
                ))}
              </TableRow>

              <TableRow>
                <TableCell className="text-xs font-medium text-muted-foreground sticky left-0 bg-background py-1.5">
                  Location
                </TableCell>
                {weddings.map((wedding) => (
                  <TableCell key={wedding.id} className="text-center text-xs py-1.5">
                    {wedding.location}
                  </TableCell>
                ))}
              </TableRow>

              <TableRow>
                <TableCell className="text-xs font-medium text-muted-foreground sticky left-0 bg-background py-1.5">
                  Year
                </TableCell>
                {weddings.map((wedding) => (
                  <TableCell key={wedding.id} className="text-center text-xs py-1.5">
                    {wedding.weddingYear}
                  </TableCell>
                ))}
              </TableRow>

              {/* Category Sections with Line Items */}
              {groupedItems.map(({ category, items }) => (
                <React.Fragment key={category}>
                  {/* Category Header */}
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableCell className="py-1.5 sticky left-0 bg-muted/50">
                      <Badge
                        variant="secondary"
                        className={`${CATEGORY_COLORS[category] || CATEGORY_COLORS.OTHER} text-[10px] px-1.5 py-0`}
                      >
                        {BUDGET_CATEGORY_LABELS[category] || category}
                      </Badge>
                    </TableCell>
                    {weddings.map((wedding) => {
                      const total = getCategoryTotal(wedding, category);
                      const pct = wedding.totalSpend > 0 
                        ? ((total / wedding.totalSpend) * 100).toFixed(0) 
                        : '0';
                      return (
                        <TableCell
                          key={wedding.id}
                          className="text-center font-mono text-xs py-1.5 font-semibold"
                        >
                          {total > 0 ? (
                            <>
                              ${total.toLocaleString()}
                              <span className="text-muted-foreground font-normal ml-1">
                                ({pct}%)
                              </span>
                            </>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>

                  {/* Line Items in Category */}
                  {items.map((itemName) => (
                    <TableRow key={`${category}-${itemName}`} className="hover:bg-accent/30">
                      <TableCell className="text-xs text-muted-foreground pl-4 sticky left-0 bg-background py-1">
                        {itemName}
                      </TableCell>
                      {weddings.map((wedding) => {
                        const amount = getItemAmount(wedding, category, itemName);
                        return (
                          <TableCell
                            key={wedding.id}
                            className="text-center font-mono text-xs py-1"
                          >
                            {amount !== null ? (
                              `$${amount.toLocaleString()}`
                            ) : (
                              <span className="text-muted-foreground/50">—</span>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
}

